import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { environment } from '../../../environments/environment';
import { Observable, ReplaySubject, Subscription } from 'rxjs';
import { from } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { Router, NavigationEnd, NavigationSkipped } from '@angular/router';
import { Hub } from 'aws-amplify/utils';
import { fetchAuthSession, signInWithRedirect, signOut } from 'aws-amplify/auth';
import { IUser } from '../../models/IUser';
import { UserActions } from 'src/app/store/user.actions';
import { Store } from '@ngxs/store';
import { post } from 'aws-amplify/api';
// const axios = require('axios').default;
import axios, { AxiosHeaders } from 'axios';
@Injectable({
    providedIn: 'root'
})
export class AuthenticationService implements OnDestroy {
    private readonly rolesSubject = new ReplaySubject<string[]>(1);

    /** Read-only stream whenever Cognito roles have been resolved or cleared (e.g. sign-out). */
    readonly roles$: Observable<string[]> = this.rolesSubject.asObservable();

    private login: boolean = false;
    private roles: Array<string> = [];
    private isLoggingOut = false;
    readonly idpProvider: string = environment.idpCognito;
    paramObsRoute: Subscription;
    private readonly initialUrl: string;

    constructor(private route: Router, private zone: NgZone, private store: Store) {
        this.initialUrl = window.location.pathname + window.location.search + window.location.hash;
        this.start();
        this.requestInterceptors();
        this.routeObs();
        if (
            window.location.href === `${environment.aws.oauth.redirectUrl}/` &&
            !window?.location?.href.split('code=')[1] &&
            !localStorage.hasOwnProperty(
                `CognitoIdentityServiceProvider.${environment.aws.userPoolWebClientId}.LastAuthUser`
            )
        ) {
            this.socialLogin(environment.idpCognito);
        }
        Hub.listen('auth', ({ payload }) => {
            switch (payload.event) {
                //  v6 successful sign-in
                case 'signedIn':
                case 'signInWithRedirect':
                    this.login = true;
                    this.handleUserRedirect();
                    break;
                //  v6 sign-out
                case 'signedOut':
                    this.login = false;
                    this.roles = [];
                    this.emitRoles();
                    if (!this.isLoggingOut) {
                        this.route.navigate(['logout'], { replaceUrl: true });
                    }
                    break;
                // v6 failures
                case 'signInWithRedirect_failure':
                case 'tokenRefresh_failure':
                    // case 'signedOut_failure':
                    this.login = false;
                    this.roles = [];
                    this.emitRoles();
                    this.zone.run(() => {
                        if (window.location.pathname !== '/logout') {
                            this.route.navigate(['login']);
                        }
                    });
                    break;
                default:
                    break;
            }
        });
        this.handleUserRedirect();
    }
    public socialLogin(provider: string): void {
        fetchAuthSession()
            .then((session) => {
                if (window.location.pathname === '/logout' && session.tokens) {
                    this.handleUserRedirect(true);
                    return;
                }
                signInWithRedirect({ provider: { custom: provider } })
                    .catch(() => {
                        this.signOut();
                    });
            })
            .catch(() => {
                signInWithRedirect({ provider: { custom: provider } })
                    .catch(() => {
                        this.signOut();
                    });
            });
    }
    handleUserAuth(): void {
        if (this.login) {
            this.handleUserRedirect();
        }
    }

    getUserRoles(): string[] {
        return this.roles;
    }

    private emitRoles(): void {
        this.zone.run(() => this.rolesSubject.next([...this.roles]));
    }
    getUserEmail(): Observable<string> {
        return from(
            (async () => {
                const session = await fetchAuthSession();
                if (!session.tokens) {
                    // Avoid routing to /login while on /logout with a live session (e.g. Back from app);
                    // a parallel handleUserRedirect can still navigate to the app.
                    if (window.location.pathname !== '/logout') {
                        this.route.navigate(['/login']);
                    }
                    return '';
                }
                const email = session.tokens.idToken?.payload?.['email'] as string | undefined;
                return email ?? '';
            })()
        );
    }

    private setUserState(): void {
        this.getUser().pipe(take(1)).subscribe(user => {
            this.store.dispatch(new UserActions.SetUser(user));
        });
    }
    getUser(): Observable<IUser> {
        return this.getUserEmail().pipe(
            switchMap((email) => {
                const payload = { email };
                const op = post({
                    apiName: 'portal',
                    path: 'persons',
                    options: { body: payload as Record<string, any> },
                });
                return from(op.response.then(async (r) => (await r.body.json()) as unknown as IUser));
            })
        );
    }

    signOut(): void {
        if (this.isLoggingOut) return;
        this.isLoggingOut = true;

        localStorage.setItem('logout-event', Math.random().toString());

        signOut()
            .catch(() => {
                // Optional: handle/log the error while still ensuring Cognito logout is attempted.
                this.zone.run(() => {
                    this.isLoggingOut = false;
                    this.login = false;
                    this.roles = [];
                    this.emitRoles();
                    this.route.navigate(['/logout'], { replaceUrl: true });
                });
            })
            .finally(() => {
                this.redirectToCognitoLogout();
            });
    }

    /** Re-runs the Cognito logout redirect if sign-out has started but the page is still alive (e.g. redirect cancelled). */
    retryLogoutRedirect(): void {
        if (this.isLoggingOut) {
            this.redirectToCognitoLogout();
        }
    }

    private redirectToCognitoLogout(): void {
        const domain = environment.aws.oauth.domain;
        const clientId = environment.aws.userPoolWebClientId;
        const logoutUri = encodeURIComponent(environment.aws.oauth.redirectLogoutUrl);
        window.location.href = `https://${domain}/logout?client_id=${clientId}&logout_uri=${logoutUri}`;
    }
    /**
     * @param allowFromLogout When true (only from user click on /logout with a session), run redirect
     *        logic even on /logout. Constructor/Hub use false so /logout is a stable shell until the user acts.
     */
    private handleUserRedirect(allowFromLogout = false): void {
        if (!allowFromLogout && window.location.pathname === '/logout') {
            return;
        }

        fetchAuthSession()
            .then((session) => {
                const deferSetUserStateUntilAfterNav =
                    allowFromLogout && window.location.pathname === '/logout' && !!session.tokens;
                if (!deferSetUserStateUntilAfterNav) {
                    this.setUserState();
                }
                const payload = session.tokens?.idToken?.payload ?? {};
                const idpRole = payload['custom:Role'] as string | undefined;
                // Cognito groups can be string[] or string depending on token/TS
                const cognitoGroupsRaw = payload['cognito:groups'] as string[] | string | undefined;
                const cognitoGroups: string[] = Array.isArray(cognitoGroupsRaw)
                    ? cognitoGroupsRaw
                    : cognitoGroupsRaw
                        ? [cognitoGroupsRaw]
                        : [];
                if (idpRole) {
                    this.roles = [idpRole];
                } else if (cognitoGroups.length) {
                    this.roles = cognitoGroups;
                } else {
                    const stored = localStorage.getItem('role');
                    this.roles = stored ? [stored] : [];
                }
                this.emitRoles();
                const targetUrl = this.pickBestTargetUrl();
                this.zone.run(() => {
                    let navPromise: Promise<boolean> | null = null;
                    // avoiding re-navigation loops
                    if (targetUrl && this.route.url !== targetUrl) {
                        navPromise = this.route.navigateByUrl(targetUrl, {
                            replaceUrl: this.shouldReplaceHistoryWhenNavigatingTo(targetUrl),
                        });
                    } else if (!targetUrl || this.route.url === '/' || this.route.url === '') {
                        const fallback = this.defaultRouteByRole();
                        navPromise = this.route.navigateByUrl(fallback, {
                            replaceUrl: this.shouldReplaceHistoryWhenNavigatingTo(fallback),
                        });
                    }
                    if (deferSetUserStateUntilAfterNav) {
                        if (navPromise) {
                            navPromise
                                .then(() => this.setUserState())
                                .catch(() => this.setUserState());
                        } else {
                            this.setUserState();
                        }
                    }
                });
            })
            .catch(() => {
                this.roles = [];
                this.emitRoles();
                this.zone.run(() => this.route.navigate(['error/401']));
            });
    }

    private pickBestTargetUrl(): string | null {
        const routerUrl = this.route.url;
        const browserUrl = window.location.pathname + window.location.search + window.location.hash;
        const current = (routerUrl && routerUrl !== '/' && routerUrl !== '')
            ? routerUrl
            : (browserUrl && browserUrl !== '/' && browserUrl !== '')
                ? browserUrl
                : this.initialUrl;
        // blocklist routes you never want to "restore"
        if (this.isBlockedRestoreUrl(current)) {
            // fall back to previousUrl if exists
            const prev = window.sessionStorage.getItem('previousUrl');
            if (prev && !this.isBlockedRestoreUrl(prev)) return prev;
            return null;
        }
        // If user refreshed on a valid in-app route, stay there
        if (current && current !== '/' && current !== '') return current;
        const prev = window.sessionStorage.getItem('previousUrl');
        if (prev && !this.isBlockedRestoreUrl(prev)) return prev;
        return null;
    }
    private isBlockedRestoreUrl(url: string): boolean {
        if (!url) return true;
        // normalize
        const u = url.toLowerCase();
        // avoid restoring these
        if (u === '/logout' || u.startsWith('/error') || u === '/login') return true;
        // avoid redirect/callback pages (code=)
        if (u.includes('code=')) return true;
        return false;
    }
    getHomeRoute(): string {
        return this.defaultRouteByRole();
    }

    private defaultRouteByRole(): string {
        // keep your original mappings
        if (this.roles.includes('participant')) return '/journey/home';
        if (this.roles.includes('facilitator')) return '/admin/programs-external';
        if (this.roles.includes('flightReport') || this.roles.includes('FlightReport')) return '/admin/reports/arrival';
        if (this.roles.includes('admin') || this.roles.includes('restrictedadmin')) return '/admin';
        return '/error';
    }

    /**
     * When auth sends the user into Admin from the root/Journey/login shell, replace the current
     * history entry so browser Back does not land on an implicit /journey/home redirect.
     * Also replace when leaving /logout or /login so Back from the app returns to prior routes (e.g. dashboard).
     */
    private shouldReplaceHistoryWhenNavigatingTo(targetUrl: string): boolean {
        const targetPath = targetUrl.split(/[?#]/)[0].toLowerCase();
        const pathname = (typeof window !== 'undefined' ? window.location.pathname : '').toLowerCase();

        if (pathname === '/logout' || pathname === '/login') {
            return !targetPath.startsWith('/error');
        }

        const isAdminTarget = targetPath === '/admin' || targetPath.startsWith('/admin/');
        if (!isAdminTarget) {
            return false;
        }
        return pathname === '/' || pathname === '/login' || pathname.startsWith('/journey');
    }

    private requestInterceptors(): void {
        axios.interceptors.request.use(
            async (config) => {
                try {
                    const session = await fetchAuthSession();
                    const token = session.tokens?.accessToken?.toString();
                    if (!token) {
                        this.signOut();
                        return config;
                    }

                    config.headers = config.headers ?? new AxiosHeaders();
                    (config.headers as AxiosHeaders).set('Authorization', token);
                    return config;
                } catch (e) {
                    this.signOut();
                    return config;
                }
            },
            (error) => Promise.reject(error)
        );
    }

    // Sign Out all tabs/windows when logged out of any tab.
    public signIn = () => {
        this.login = true;
        this.route.navigate([''])
    }

    public logOut = () => {
        this.login = false;
        const url = this.route.url.split(/[?#]/)[0];
        if (url !== '/logout' && url !== 'logout') {
            this.route.navigate(['logout'], { replaceUrl: true });
        }
    }

    private start(): void {
        window.addEventListener("storage", this.storageEventListener);
    }

    private storageEventListener = (event: StorageEvent): void => {
        if (event.storageArea == localStorage) {
            if (event?.key && event.key == 'logout-event') {
                this.logOut()
            }
        }
    };

    private stop(): void {
        window.removeEventListener("storage", this.storageEventListener);
    }
    /**
     * Keeps the last visited URL in sessionStorage so refresh restores it.
     * (Same logic as old)
     */
    public routeObs() {
        // prevent duplicate subscriptions if service gets reinstantiated somehow
        if (this.paramObsRoute) return;
        this.paramObsRoute = this.route.events.subscribe((event) => {
            if (
                (event instanceof NavigationEnd || event instanceof NavigationSkipped) &&
                event.url !== '/logout' &&
                !event.url.includes('/error') &&
                event.url !== '/login'
            ) {
                window.sessionStorage.setItem('previousUrl', event.url);
            }
        });
    }

    ngOnDestroy() {
        this.stop()
        this.paramObsRoute.unsubscribe();
    }
}
